import { DefinedEvent, getDefinedEvents } from '@fullstory/events-ui';
import {
  AppDispatch,
  DefinedEventsRequestActions,
  fullstorySelectors,
  getActiveHeatmaps,
  useActiveSavedFunnels,
  useActiveSavedMetrics,
  useAllRetentionMetrics,
  useFetchAllFunnels,
  useFetchAllHeatmaps,
  useFetchAllJourneys,
  useFetchAllMetrics,
  useFetchAllSegments,
  useJourneys,
} from '@fullstory/redux';
import { getActiveSegmentsById } from '@fullstory/redux/modules/segments/selectors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LibraryObject } from './util';
import { Timestamp, timestamp } from '@fullstory/proto/google/protobuf';

export const timestampToDate = (ts: timestamp | null): Date => Timestamp.toDate(ts) ?? new Date();

type Object = { str: string; id: string; name: string; lastUpdated: Date; createdBy: string };

const objectSearch = (
  searchString: string,
  objects: Object[],
  setLoaded: (type: string) => void,
  type: LibraryObject['type'],
  loaded: { [key: string]: boolean },
  matches: LibraryObject[],
) => {
  if (!loaded[type] && objects.length) {
    objects.map(obj => {
      // assume searchString is an id, so we don't match on it
      if (obj.str.includes(searchString) && searchString !== obj.id)
        matches.push({
          type,
          id: obj.id,
          name: obj.name,
          lastUpdated: obj.lastUpdated,
          createdBy: obj.createdBy,
        });
    });
    setLoaded(type);
  }
};

export const useObjectSearch = (searchString: string) => {
  const orgId = useSelector(fullstorySelectors.getOrgId);

  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    dispatch(DefinedEventsRequestActions.getAllDefinedEventsRequest(orgId));
  }, [orgId, dispatch]);

  const definedEvents = useSelector(getDefinedEvents) as DefinedEvent[];

  useFetchAllFunnels(orgId);
  const { activeFunnels } = useActiveSavedFunnels();

  useFetchAllHeatmaps(orgId, false);
  const hm = useSelector(getActiveHeatmaps);

  const { byId: journeysById } = useJourneys();
  useFetchAllJourneys(orgId, false);

  const { byId: metricsById } = useActiveSavedMetrics();
  useFetchAllMetrics(orgId);

  const { byId: retentionsById } = useAllRetentionMetrics();

  useFetchAllSegments(orgId);
  const segmentsById = useSelector(getActiveSegmentsById);

  const events = useMemo(
    () =>
      definedEvents.map(de => ({
        str: JSON.stringify(de.events),
        id: de.id,
        name: de.name,
        lastUpdated: timestampToDate(de.auditMetadata?.lastUpdated || null),
        createdBy: de.auditMetadata?.createdBy!,
      })),
    [definedEvents],
  );
  const funnels = useMemo(
    () =>
      activeFunnels.map(f => ({
        str: JSON.stringify(f.funnel?.funnel),
        id: f.funnel?.funnel?.id!,
        name: f.funnel?.funnel?.name || 'unnamed',
        lastUpdated: timestampToDate(f.funnel?.metadata?.lastUpdated || null),
        createdBy: f.funnel?.metadata?.createdBy!,
      })),
    [activeFunnels],
  );
  const heatmaps = useMemo(
    () =>
      hm.map(h => ({
        str: JSON.stringify(h.heatmap?.heatmap),
        id: h.heatmap?.heatmap?.id!,
        name: h.heatmap?.heatmap?.name || 'unnamed',
        lastUpdated: timestampToDate(h.heatmap?.metadata?.lastUpdated || null),
        createdBy: h.heatmap?.metadata?.createdBy!,
      })),
    [hm],
  );
  const journeys = useMemo(
    () =>
      Object.values(journeysById).map(j => ({
        str: JSON.stringify(j.journey?.journeyMap),
        id: j.journey?.journeyMap?.id!,
        name: j.journey?.journeyMap?.name || 'unnamed',
        lastUpdated: timestampToDate(j.journey?.metadata?.lastUpdated || null),
        createdBy: j.journey?.metadata?.createdBy!,
      })),
    [journeysById],
  );
  const metrics = useMemo(
    () =>
      Object.values(metricsById).map(m => ({
        str: JSON.stringify(m.metric?.metric),
        id: m.metric?.metric?.id!,
        name: m.metric?.metric?.name || 'unnamed',
        lastUpdated: timestampToDate(m.metric?.metadata?.lastUpdated || null),
        createdBy: m.metric?.metadata?.createdBy!,
      })),
    [metricsById],
  );
  const retentions = useMemo(
    () =>
      Object.values(retentionsById).map(r => ({
        str: JSON.stringify(r.retentionMetric?.retentionMetric),
        id: r.retentionMetric?.retentionMetric?.id!,
        name: r.retentionMetric?.retentionMetric?.name || 'unnamed',
        lastUpdated: timestampToDate(r.retentionMetric?.metadata?.lastUpdated || null),
        createdBy: r.retentionMetric?.metadata?.createdBy!,
      })),
    [retentionsById],
  );
  const segments = useMemo(
    () =>
      Object.values(segmentsById).map(s => ({
        str: JSON.stringify(s.segment?.segment?.segmentDefinition),
        id: s.segment?.segment?.id!,
        name: s.segment?.segment?.name || 'unnamed',
        lastUpdated: timestampToDate(s.segment?.metadata?.lastUpdated || null),
        createdBy: s.segment?.metadata?.createdBy!,
      })),
    [segmentsById],
  );

  const [allMatches, setAllMatches] = useState<LibraryObject[]>([]);
  const [loaded, setLoaded] = useState<{ [key: string]: boolean }>({});

  // Use refs to track previous values to prevent unnecessary re-renders
  const prevSearchStringRef = useRef<string>('');
  const prevArraysRef = useRef<{
    events: Object[];
    funnels: Object[];
    heatmaps: Object[];
    journeys: Object[];
    metrics: Object[];
    retentions: Object[];
    segments: Object[];
  }>({
    events: [],
    funnels: [],
    heatmaps: [],
    journeys: [],
    metrics: [],
    retentions: [],
    segments: [],
  });

  // Reset loaded state when searchString changes
  useEffect(() => {
    if (searchString !== prevSearchStringRef.current) {
      setLoaded({});
      setAllMatches([]);
      prevSearchStringRef.current = searchString;
    }
  }, [searchString]);

  const setLoadedForType = useCallback((str: string) => {
    setLoaded(prev => ({ ...prev, [str]: true }));
  }, []);

  // Main search effect with proper dependency management
  useEffect(() => {
    if (searchString.length < 5) return;

    // Check if any arrays have actually changed
    const currentArrays = { events, funnels, heatmaps, journeys, metrics, retentions, segments };
    const hasArrayChanged = Object.keys(currentArrays).some(
      key =>
        currentArrays[key as keyof typeof currentArrays] !==
        prevArraysRef.current[key as keyof typeof currentArrays],
    );

    if (!hasArrayChanged && searchString === prevSearchStringRef.current) {
      return;
    }

    prevArraysRef.current = currentArrays;

    const matches: LibraryObject[] = [];

    objectSearch(searchString, events, setLoadedForType, 'EVENT', loaded, matches);
    objectSearch(searchString, funnels, setLoadedForType, 'FUNNEL', loaded, matches);
    objectSearch(searchString, heatmaps, setLoadedForType, 'HEATMAP', loaded, matches);
    objectSearch(searchString, journeys, setLoadedForType, 'JOURNEY', loaded, matches);
    objectSearch(searchString, metrics, setLoadedForType, 'METRIC', loaded, matches);
    objectSearch(searchString, retentions, setLoadedForType, 'RETENTION', loaded, matches);
    objectSearch(searchString, segments, setLoadedForType, 'SEGMENT', loaded, matches);

    setAllMatches(matches);
  }, [
    searchString,
    events,
    funnels,
    heatmaps,
    journeys,
    metrics,
    retentions,
    segments,
    setLoadedForType,
  ]);

  return {
    events,
    funnels,
    heatmaps,
    journeys,
    metrics,
    retentions,
    segments,
    matches: allMatches,
  };
};
